// ============================================================
// CollegeDashboard.jsx — Main Site Dashboard (Port 3000)
// Accessed AFTER payment. College admin monitors everything here.
// ============================================================
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Building2, MapPin, Mail, Phone, Globe, Edit, ExternalLink,
  Settings, BarChart3, Users, Star, BookOpen, Loader2,
  CheckCircle2, AlertCircle, TrendingUp, Eye, Calendar,
  Award, Wifi, Library, ShoppingBag, Beaker, Hotel,
} from "lucide-react";
import { collegeFormFillApi } from "../../services/collegeFormFillApi";
import { secureLinkApi } from "../../services/secureLinkApi";
import { getDeviceFingerprint } from "../../utils/device";

const PUBLIC_FORM_URL = process.env.REACT_APP_PUBLIC_FORM_URL || "http://localhost:3001";

// ── Stat Card ────────────────────────────────────────────────
function StatCard({ icon: Icon, label, value, color = "blue", trend }) {
  const colors = {
    blue: "bg-blue-50 text-blue-600 border-blue-100",
    green: "bg-green-50 text-green-600 border-green-100",
    purple: "bg-purple-50 text-purple-600 border-purple-100",
    amber: "bg-amber-50 text-amber-600 border-amber-100",
  };
  return (
    <div className={`bg-white rounded-2xl border p-5 shadow-sm hover:shadow-md transition-all ${colors[color].split(" ").slice(2).join(" ")}`}>
      <div className="flex items-center justify-between mb-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${colors[color].split(" ").slice(0, 2).join(" ")}`}>
          <Icon className="w-5 h-5" />
        </div>
        {trend && (
          <span className="text-xs font-semibold text-green-600 bg-green-50 px-2 py-0.5 rounded-full flex items-center gap-1">
            <TrendingUp className="w-3 h-3" /> {trend}
          </span>
        )}
      </div>
      <p className="text-2xl font-extrabold text-gray-900">{value}</p>
      <p className="text-sm text-gray-500 mt-0.5">{label}</p>
    </div>
  );
}

// ── Info Row ─────────────────────────────────────────────────
function InfoRow({ icon: Icon, label, value, color = "text-gray-400" }) {
  if (!value) return null;
  return (
    <div className="flex items-center gap-3 py-2.5 border-b border-gray-50 last:border-0">
      <Icon className={`w-4 h-4 ${color} flex-shrink-0`} />
      <span className="text-xs text-gray-400 w-24 flex-shrink-0">{label}</span>
      <span className="text-sm font-medium text-gray-700 truncate">{value}</span>
    </div>
  );
}

// ── Facility Badge ────────────────────────────────────────────
function FacilityBadge({ icon: Icon, label, active }) {
  return (
    <div className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-xs font-medium transition-all ${
      active
        ? "bg-green-50 border-green-200 text-green-700"
        : "bg-gray-50 border-gray-200 text-gray-400"
    }`}>
      <Icon className="w-3.5 h-3.5" />
      {label}
      {active && <CheckCircle2 className="w-3 h-3 ml-auto" />}
    </div>
  );
}

export default function CollegeDashboard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const showUpdated = searchParams.get("updated") === "true";

  const [admin, setAdmin] = useState(null);
  const [college, setCollege] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasCollege, setHasCollege] = useState(false);
  const [toast, setToast] = useState(showUpdated ? "Profile updated successfully!" : "");

  useEffect(() => {
    loadDashboard();
    if (showUpdated) setTimeout(() => setToast(""), 4000);
  }, []);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const adminRes = await collegeFormFillApi.getAdminProfile();
      if (!adminRes.success) return;

      const adminData = adminRes.user || adminRes.data;
      setAdmin(adminData);

      const collegeId = adminRes.collegeId || adminData?.collegeId;
      if (collegeId) {
        const cRes = await collegeFormFillApi.getCollegeDetails(collegeId);
        if (cRes.success && cRes.college) {
          setCollege(cRes.college);
          setHasCollege(true);
        }
      }
    } catch (err) {
      console.error("Dashboard error:", err);
    } finally {
      setLoading(false);
    }
  };

  // ── Generate secure link → open form on 3001 ─────────────
  const openSecureForm = async (purpose) => {
    try {
      const deviceId = getDeviceFingerprint();
      const res = await secureLinkApi.generateSecureLink(purpose);
      const { token, expiresAt } = res;

      const url = purpose === "edit" && college?._id
        ? `${PUBLIC_FORM_URL}/form/edit?auth=${token}&collegeId=${college._id}&device=${deviceId}`
        : `${PUBLIC_FORM_URL}/form/fill?auth=${token}&device=${deviceId}&exp=${encodeURIComponent(expiresAt)}`;

      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      console.error("Secure link error:", err);
      alert("Unable to generate secure link. Please try again.");
    }
  };

  // ── Loading ────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600 font-medium">Loading your dashboard…</p>
        </div>
      </div>
    );
  }

  // ── No College — First Time ─────────────────────────────────
  if (!hasCollege) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center p-6">
        <div className="bg-white shadow-2xl rounded-3xl p-10 max-w-lg w-full text-center">
          <div className="w-24 h-24 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-xl">
            <Building2 className="w-12 h-12 text-white" />
          </div>
          <h1 className="text-2xl font-extrabold text-gray-900 mb-3">
            Welcome, {admin?.name || "Administrator"}! 👋
          </h1>
          <p className="text-gray-500 mb-2 leading-relaxed text-sm">
            You haven't registered your institution yet. Complete your college profile to get listed on Vidyazon and start receiving student enquiries.
          </p>
          <div className="grid grid-cols-3 gap-3 my-6 text-center">
            {[["5 min", "to register"], ["Free", "profile page"], ["Live", "same day"]].map(([val, lbl]) => (
              <div key={lbl} className="bg-blue-50 rounded-xl p-3">
                <p className="text-blue-700 font-extrabold text-lg">{val}</p>
                <p className="text-blue-500 text-xs">{lbl}</p>
              </div>
            ))}
          </div>
          <button
            onClick={() => openSecureForm("fill")}
            className="w-full py-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-2xl font-bold text-base shadow-xl shadow-blue-200 transition-all flex items-center justify-center gap-2"
          >
            <ExternalLink className="w-5 h-5" />
            Register Your College Now
          </button>
          <p className="text-xs text-gray-400 mt-3">
            A secure, device-locked form will open in a new tab.
          </p>
        </div>
      </div>
    );
  }

  // ── Main Dashboard ─────────────────────────────────────────
  const courses = college.courses || [];
  const ugCourses = courses.filter((c) => c.type?.toLowerCase() === "ug");
  const pgCourses = courses.filter((c) => c.type?.toLowerCase() === "pg");

  return (
    <div className="min-h-screen bg-gray-50">

      {/* Toast */}
      {toast && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 bg-green-600 text-white px-5 py-3 rounded-2xl shadow-2xl text-sm font-medium animate-in fade-in slide-in-from-top-2">
          <CheckCircle2 className="w-5 h-5" />
          {toast}
        </div>
      )}

      {/* ── Top Header ───────────────────────────────────────── */}
      <div className="bg-gradient-to-r from-blue-700 via-blue-600 to-indigo-700 text-white shadow-xl">
        <div className="max-w-6xl mx-auto px-6 py-6">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-5 justify-between">

            {/* College Identity */}
            <div className="flex items-center gap-5">
              {college.collegeLogo || college.logo ? (
                <img
                  src={college.collegeLogo || college.logo}
                  alt="Logo"
                  className="w-16 h-16 rounded-2xl object-cover shadow-lg border-2 border-white/30"
                />
              ) : (
                <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center text-3xl font-extrabold shadow-inner">
                  {college.collegeName?.charAt(0)}
                </div>
              )}
              <div>
                <h1 className="text-2xl font-extrabold">{college.collegeName}</h1>
                <div className="flex items-center gap-2 mt-1">
                  <MapPin className="w-3.5 h-3.5 text-blue-200" />
                  <span className="text-blue-200 text-sm">{college.city}, {college.state}</span>
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <span className="px-2.5 py-1 bg-green-500/20 border border-green-400/30 text-green-300 text-xs rounded-full font-semibold">
                    ✓ Active & Verified
                  </span>
                  {college.accreditation && (
                    <span className="px-2.5 py-1 bg-amber-500/20 border border-amber-400/30 text-amber-300 text-xs rounded-full font-semibold">
                      {college.accreditation}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => openSecureForm("edit")}
                className="flex items-center gap-2 px-5 py-2.5 bg-white text-blue-700 rounded-xl font-bold text-sm shadow hover:shadow-md transition-all"
              >
                <Edit className="w-4 h-4" /> Edit Profile
              </button>
              {college._id && (
                <button
                  onClick={() => window.open(`/colleges/${college._id}`, "_blank")}
                  className="flex items-center gap-2 px-5 py-2.5 bg-white/20 hover:bg-white/30 border border-white/30 text-white rounded-xl font-semibold text-sm transition-all"
                >
                  <Eye className="w-4 h-4" /> View Live Page
                </button>
              )}
              <button
                onClick={() => navigate("/settings")}
                className="flex items-center gap-2 px-4 py-2.5 bg-white/10 hover:bg-white/20 border border-white/20 text-white rounded-xl font-semibold text-sm transition-all"
              >
                <Settings className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8 space-y-6">

        {/* ── Stats Row ─────────────────────────────────────── */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            icon={BookOpen} color="blue"
            label="Total Courses" value={courses.length || 0}
          />
          <StatCard
            icon={Star} color="amber"
            label="Rating" value={college.rating ? `${college.rating}/5` : "N/A"}
          />
          <StatCard
            icon={Users} color="green"
            label="Total Reviews" value={college.totalReviews || 0}
          />
          <StatCard
            icon={TrendingUp} color="purple"
            label="Placement Rate" value={college.placementRate || "N/A"}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* ── LEFT: Profile Details ─────────────────────── */}
          <div className="lg:col-span-2 space-y-5">

            {/* Contact & Info Card */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="flex items-center gap-3 px-6 py-4 bg-gray-50 border-b border-gray-100">
                <Building2 className="w-5 h-5 text-blue-600" />
                <h3 className="font-bold text-gray-800">Institution Details</h3>
              </div>
              <div className="px-6 py-4">
                <InfoRow icon={Mail} label="Email" value={college.email} color="text-blue-400" />
                <InfoRow icon={Phone} label="Mobile" value={college.mobile || college.phone} color="text-green-400" />
                <InfoRow icon={Globe} label="Website" value={college.website} color="text-purple-400" />
                <InfoRow icon={MapPin} label="Address" value={college.address} color="text-rose-400" />
                <InfoRow icon={Award} label="Affiliation" value={college.affiliations} color="text-amber-400" />
                <InfoRow icon={Calendar} label="Established" value={college.established} color="text-indigo-400" />
              </div>
            </div>

            {/* Courses Card */}
            {courses.length > 0 && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="flex items-center justify-between px-6 py-4 bg-gray-50 border-b border-gray-100">
                  <div className="flex items-center gap-3">
                    <BookOpen className="w-5 h-5 text-indigo-600" />
                    <h3 className="font-bold text-gray-800">Courses Offered</h3>
                  </div>
                  <span className="bg-indigo-100 text-indigo-700 text-xs font-bold px-2.5 py-1 rounded-full">
                    {courses.length} courses
                  </span>
                </div>

                {ugCourses.length > 0 && (
                  <div className="px-6 py-4 border-b border-gray-50">
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-3">
                      Undergraduate ({ugCourses.length})
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {ugCourses.slice(0, 6).map((c, i) => (
                        <div key={i} className="flex items-center justify-between bg-gray-50 rounded-xl p-3 border border-gray-100">
                          <div>
                            <p className="text-sm font-semibold text-gray-800">{c.name}</p>
                            <p className="text-xs text-gray-400">{c.duration}</p>
                          </div>
                          {c.fees && (
                            <span className="text-xs font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-lg">
                              {c.fees}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {pgCourses.length > 0 && (
                  <div className="px-6 py-4">
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-3">
                      Postgraduate ({pgCourses.length})
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {pgCourses.slice(0, 4).map((c, i) => (
                        <div key={i} className="flex items-center justify-between bg-gray-50 rounded-xl p-3 border border-gray-100">
                          <div>
                            <p className="text-sm font-semibold text-gray-800">{c.name}</p>
                            <p className="text-xs text-gray-400">{c.duration}</p>
                          </div>
                          {c.fees && (
                            <span className="text-xs font-bold text-purple-600 bg-purple-50 px-2 py-0.5 rounded-lg">
                              {c.fees}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Placement Card */}
            {(college.averagePackage || college.highestPackage || college.topRecruiters) && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="flex items-center gap-3 px-6 py-4 bg-gray-50 border-b border-gray-100">
                  <BarChart3 className="w-5 h-5 text-green-600" />
                  <h3 className="font-bold text-gray-800">Placement Statistics</h3>
                </div>
                <div className="p-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {[
                    ["Average Package", college.averagePackage],
                    ["Highest Package", college.highestPackage],
                    ["Placement Rate", college.placementRate],
                  ].filter(([, v]) => v).map(([label, value]) => (
                    <div key={label} className="bg-green-50 rounded-xl p-4 text-center border border-green-100">
                      <p className="text-xl font-extrabold text-green-700">{value}</p>
                      <p className="text-xs text-green-600 mt-1">{label}</p>
                    </div>
                  ))}
                </div>
                {college.topRecruiters && (
                  <div className="px-6 pb-5">
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-2">Top Recruiters</p>
                    <p className="text-sm text-gray-600">{college.topRecruiters}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── RIGHT: Sidebar ────────────────────────────── */}
          <div className="space-y-5">

            {/* Admin Info */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="flex items-center gap-3 px-5 py-4 bg-gray-50 border-b border-gray-100">
                <Users className="w-5 h-5 text-green-600" />
                <h3 className="font-bold text-gray-800 text-sm">Administrator</h3>
              </div>
              <div className="p-5">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-emerald-500 rounded-xl flex items-center justify-center text-white font-bold text-lg">
                    {admin?.name?.charAt(0) || "A"}
                  </div>
                  <div>
                    <p className="font-bold text-gray-800 text-sm">{admin?.name || "Administrator"}</p>
                    <p className="text-xs text-gray-400">College Admin</p>
                  </div>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-gray-600">
                    <Mail className="w-3.5 h-3.5 text-gray-400" />
                    <span className="text-xs truncate">{admin?.email}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
                    <span className="text-xs text-green-600 font-semibold">Active Account</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Facilities */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="flex items-center gap-3 px-5 py-4 bg-gray-50 border-b border-gray-100">
                <Award className="w-5 h-5 text-amber-600" />
                <h3 className="font-bold text-gray-800 text-sm">Campus Facilities</h3>
              </div>
              <div className="p-5 grid grid-cols-2 gap-2">
                <FacilityBadge icon={Hotel} label="Hostel" active={college.hostel} />
                <FacilityBadge icon={Library} label="Library" active={college.library} />
                <FacilityBadge icon={Beaker} label="Labs" active={college.labs} />
                <FacilityBadge icon={ShoppingBag} label="Cafeteria" active={college.cafeteria} />
                <FacilityBadge icon={Wifi} label="Sports" active={college.sports} />
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl p-5 text-white">
              <h3 className="font-bold mb-4 text-sm">Quick Actions</h3>
              <div className="space-y-2">
                {[
                  { icon: Edit, label: "Edit College Profile", action: () => openSecureForm("edit") },
                  { icon: Eye, label: "View Public Page", action: () => window.open(`/colleges/${college._id}`, "_blank") },
                  { icon: Settings, label: "Account Settings", action: () => navigate("/settings") },
                ].map(({ icon: Icon, label, action }) => (
                  <button
                    key={label}
                    onClick={action}
                    className="w-full flex items-center gap-3 px-4 py-2.5 bg-white/10 hover:bg-white/20 border border-white/20 rounded-xl text-sm font-medium transition-all text-left"
                  >
                    <Icon className="w-4 h-4" />
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Session Info */}
            {college.registrationDate && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-2">Registration Info</p>
                <div className="space-y-1.5">
                  <p className="text-xs text-gray-600">
                    <span className="text-gray-400">Registered:</span>{" "}
                    {new Date(college.registrationDate).toLocaleDateString("en-IN", {
                      day: "numeric", month: "short", year: "numeric",
                    })}
                  </p>
                  <p className="text-xs text-gray-600">
                    <span className="text-gray-400">Payment:</span>{" "}
                    <span className="text-green-600 font-semibold">Completed</span>
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}